<!-- src/components/aics/ImageLibrarySelector.vue -->
<template>
  <div class="image-library-selector">
    <!-- 顶部工具栏 -->
    <div class="selector-toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索图片名称或描述"
          prefix-icon="el-icon-search"
          size="small"
          style="width: 200px;"
          @keyup.enter.native="handleSearch"
        />
        <el-button 
          type="primary"
          size="small"
          @click="handleSearch"
          style="margin-left: 8px;"
        >
          搜索
        </el-button>
      </div>
      
      <div class="toolbar-right">
        <el-button 
          size="small"
          @click="handleUpload"
          icon="el-icon-upload2"
          v-if="showUpload"
        >
          上传图片
        </el-button>
        <el-button 
          size="small"
          @click="handleCancel"
        >
          取消
        </el-button>
      </div>
    </div>

    <!-- 分类筛选 -->
    <div class="category-filter" v-if="categories.length > 0">
      <el-radio-group v-model="selectedCategory" @input="handleCategoryChange" size="small">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button 
          v-for="category in categories"
          :key="category.code"
          :label="category.code"
        >
          {{ category.name }}
        </el-radio-button>
      </el-radio-group>
    </div>

    <!-- 图片网格 -->
    <div class="image-grid-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      
      <!-- 空状态 -->
      <div v-else-if="images.length === 0" class="empty-container">
        <el-empty description="暂无图片">
          <el-button type="primary" @click="handleUpload" v-if="showUpload">上传图片</el-button>
        </el-empty>
      </div>
      
      <!-- 图片网格 -->
      <div v-else class="image-grid">
        <div 
          v-for="image in images" 
          :key="image.id"
          class="image-selector-item"
          :class="{ 'selected': isSelected(image) }"
          @click="handleImageSelect(image)"
        >
          <div class="image-wrapper">
            <img 
              :src="getImageUrl(image)" 
              :alt="image.name"
              @error="handleImageError"
            />
            <div class="image-overlay">
              <el-button 
                size="mini" 
                type="primary"
                icon="el-icon-check"
                @click.stop="handleImageSelect(image)"
              >选择</el-button>
            </div>
            <div v-if="isSelected(image)" class="selected-indicator">
              <i class="el-icon-check"></i>
            </div>
          </div>
          <div class="image-info">
            <div class="image-name" :title="image.name">{{ image.name }}</div>
            <div class="image-meta">
              <span>{{ formatFileSize(image.fileSize) }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div v-if="images.length > 0" class="pagination-container">
        <el-pagination
          :current-page="pagination.current"
          :page-size="pagination.size"
          :total="pagination.total"
          layout="total, prev, pager, next, jumper"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog 
      :visible.sync="previewVisible" 
      title="图片预览"
      width="60%"
      top="5vh"
      append-to-body
    >
      <div class="image-preview">
        <img :src="previewImage.url" :alt="previewImage.name" class="preview-img" />
        <div class="preview-info">
          <h3>{{ previewImage.name }}</h3>
          <p><strong>文件大小:</strong> {{ formatFileSize(previewImage.fileSize) }}</p>
          <p><strong>上传时间:</strong> {{ formatTime(previewImage.createdAt) }}</p>
          <p v-if="previewImage.description"><strong>描述:</strong> {{ previewImage.description }}</p>
        </div>
      </div>
    </el-dialog>

    <!-- 图片上传面板 -->
    <el-dialog 
      title="上传图片"
      :visible.sync="showUploadPanel"
      width="800px"
      append-to-body
      :close-on-click-modal="false"
    >
      <div class="upload-panel">
        <!-- 文件选择区域 -->
        <div v-if="selectedFiles.length === 0" class="file-selector">
          <el-upload
            class="upload-area"
            drag
            multiple
            :auto-upload="false"
            :on-change="handleFileSelect"
            :file-list="selectedFiles"
            accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
            :show-file-list="false"
            action=""
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              将图片拖到此处，或<em>点击选择</em>
            </div>
            <div class="el-upload__tip" slot="tip">
              支持 jpg、png、gif、bmp、webp 格式，单文件不超过10MB
            </div>
          </el-upload>
        </div>

        <!-- 已选择文件预览 -->
        <div v-else class="selected-files-container">
          <div class="selected-header">
            <h4>已选择图片 ({{ selectedFiles.length }})</h4>
            <el-button 
              type="text" 
              icon="el-icon-refresh"
              @click="clearAllFiles"
            >
              重新选择
            </el-button>
          </div>
          
          <div class="file-previews">
            <div 
              v-for="(file, index) in selectedFiles" 
              :key="index"
              class="file-preview-item"
            >
              <div class="preview-container">
                <img :src="getFilePreview(file)" class="preview-img" />
                <div class="preview-overlay">
                  <el-button 
                    circle 
                    size="mini"                 
                    @click="removeSingleFile(index)"
                    class="remove-btn"
                    type="danger"
                  >
                    <i class="el-icon-delete"></i>
                  </el-button>
                </div>
              </div>
              <div class="file-info">
                <div class="file-name" :title="file.name">{{ file.name }}</div>
                <div class="file-size">{{ formatFileSize(file.size) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 图片信息表单 -->
        <div v-if="selectedFiles.length > 0" class="image-info-form">
          <el-divider content-position="left">图片信息</el-divider>
          <el-form :model="uploadForm" label-width="80px">
            <el-form-item label="图片名称">
              <el-input 
                v-model="uploadForm.name" 
                placeholder="请输入图片名称（留空使用文件名）"
              />
            </el-form-item>
            <el-form-item label="描述">
              <el-input 
                type="textarea" 
                v-model="uploadForm.description" 
                :rows="3"
                placeholder="请输入图片描述"
              />
            </el-form-item>
            <el-form-item label="标签">
              <el-input 
                v-model="uploadForm.tags" 
                placeholder="多个标签用逗号分隔"
              />
            </el-form-item>
            <el-form-item label="分类">
              <el-select v-model="uploadForm.categoryId" placeholder="选择分类" style="width: 100%">
                <el-option label="无分类" :value="null" />
                <el-option 
                  v-for="category in categories"
                  :key="category.code"
                  :label="category.name"
                  :value="category.code"
                />
              </el-select>
            </el-form-item>
          </el-form>
        </div>

        <!-- 上传进度 -->
        <div v-if="isUploading" class="upload-progress">
          <el-divider content-position="left">上传进度</el-divider>
          <el-progress
            :percentage="uploadProgress"
            :status="uploadError ? 'exception' : (uploadProgress === 100 ? 'success' : undefined)"
            :stroke-width="8"
            :show-text="true"
          />
          <div class="upload-status">
            <div v-if="isUploading && uploadProgress < 100" class="uploading-text">
              <i class="el-icon-loading"></i>
              图片上传中，请稍候...
            </div>
            <div v-else-if="uploadProgress === 100" class="success-text">
              <i class="el-icon-success"></i>
              上传完成！
            </div>
            <div v-else-if="uploadError" class="error-text">
              <i class="el-icon-error"></i>
              {{ uploadError }}
            </div>
          </div>
        </div>
      </div>
      
      <div slot="footer">
        <el-button @click="cancelUpload" :disabled="isUploading">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleSubmitUpload" 
          :disabled="selectedFiles.length === 0 || isUploading"
          :loading="isUploading"
        >
          {{ isUploading ? `上传中 (${uploadProgress}%)` : '提交上传' }}
        </el-button>
      </div>
    </el-dialog>
    
   </div>

</template>

<script>
import { getCategories, getImages, uploadImageToOSS, saveImageInfo } from '@/api/aics/image'

export default {
  name: 'ImageLibrarySelector',
  props: {
    // 选择模式：single-单选，multiple-多选
    selectionMode: {
      type: String,
      default: 'single',
      validator: value => ['single', 'multiple'].includes(value)
    },
    // 是否显示上传按钮
    showUpload: {
      type: Boolean,
      default: true
    },
    // 是否显示工具栏
    showToolbar: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 图片数据
      images: [],
      loading: false,
      searchKeyword: '',
      selectedCategory: '',
      
      // 选择相关
      selectedImages: [],
      
      // 分页
      pagination: {
        current: 1,
        size: 20,
        total: 0
      },
      
      // 分类
      categories: [],
      
      // 预览相关
      previewVisible: false,
      previewImage: {},
      
      // 上传相关
      showUploadPanel: false,
      selectedFiles: [],
      uploadForm: {
        name: '',
        description: '',
        tags: '',
        categoryId: null
      },
      isUploading: false,
      uploadProgress: 0,
      uploadError: ''
    }
  },
  computed: {
    tenantInfo() {
      return this.$store.getters['auth/currentTenant'];
    },
    tenantId() {
      return this.tenantInfo?.tenantId || 0;
    }
  },  
  created() {
    this.loadCategories(this.tenantId)
    this.loadImages()
  },
  
  methods: {
    // 加载分类
  loadCategories() {
      getCategories(this.tenantId).then(res => {
        console.log('加载分类:', res)
        if (res.data.code === 200) {
          this.categories = res.data.data
          console.log('分类数据:', this.categories)
        } else {
          this.$message.error('加载分类失败')
        }
      }).catch(() => {
        this.$message.error('加载分类失败')
      })
    },
    
    // 加载图片列表
    loadImages() {
      this.loading = true
      const params = {
        tenantId: this.tenantId,
        categoryCode: this.selectedCategory,
        keyword: this.searchKeyword,
        page: this.pagination.current - 1,
        size: this.pagination.size
      }
      
      console.log("loadImages", params);
      getImages(params).then(res => {
        console.log(res);
        this.loading = false
        if (res.data.code === 200) {
          // 完全按照 ImageLibrary.vue 的方式处理数据
          this.images = res.data.data.content || res.data.data || []
          this.pagination.total = res.data.data.totalElements || this.images.length
          console.log(this.images, this.pagination.total);
        } else {
          this.$message.error('加载图片失败')
        }
      }).catch(() => {
        this.loading = false
        this.$message.error('加载图片失败')
      })
    },
      
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadImages()
    },
    
    // 分类切换
    handleCategoryChange() {
      this.pagination.current = 1
      this.loadImages()
    },
    
    // 分页变化
    handlePageChange(page) {
      this.pagination.current = page
      this.loadImages()
    },
    
    // 获取图片URL
    getImageUrl(image) {
      // 尝试多种可能的字段名，确保能找到图片URL
      return image.temp_url || image.url || image.tempUrl || image.previewUrl || ''
    },
    
    // 图片加载失败处理
    handleImageError(event) {
      event.target.src = '/static/images/image-error.png'
    },
    // 图片点击处理
        handleImageClick(image) {
          console.log('选择图片:', image)
          
          // 直接发出选择事件
          this.$emit('image-selected', image)
          console.log('【DEBUG】选择的图片完整数据结构:', JSON.stringify(image, null, 2))
      console.log('【DEBUG】关键字段:', {
        id: image.id,
        name: image.name,
        temp_url: image.temp_url,
        tempUrl: image.tempUrl,
        url: image.url,
        originalFilename: image.originalFilename,
        ossObjectKey: image.ossObjectKey
      })
      
      // 发出选择事件
      this.$emit('image-selected', image)
    },    
    // 检查图片是否被选中
    isSelected(image) {
      return this.selectedImages.some(selected => selected.id === image.id)
    },
    
    // 选择图片
    // 确保传递完整的图片信息
    handleImageSelect(image) {
      console.log('【SELECTOR】选择图片完整对象:', image)
      // 直接传递整个图片对象，让父组件处理
      this.$emit('image-selected', image)
    },
    
    // 清空选择
    clearSelection() {
      this.selectedImages = []
    },
    
    // 确认选择
    confirmSelection() {
      if (this.selectedImages.length === 0) {
        this.$message.warning('请选择至少一张图片')
        return
      }
      
      const selectedImages = this.selectionMode === 'single' ? this.selectedImages[0] : this.selectedImages
      
      // 发出选择事件
      this.$emit('image-selected', selectedImages)
      
      // 自动关闭选择器（如果是单选模式）
      if (this.selectionMode === 'single') {
        this.$emit('cancel')
      }
    },
    
    // 取消选择
    handleCancel() {
      this.$emit('cancel')
    },
    
    // 图片预览
    handleImagePreview(image) {
      this.previewImage = {
        ...image,
        url: this.getImageUrl(image)
      }
      this.previewVisible = true
    },
    // 双击图片快速选择（新增方法）
    handleImageDoubleClick(image) {
      if (this.selectionMode === 'single') {
        this.selectedImages = [image]
        this.confirmSelection()
      }
    },    
    // 打开上传面板
    handleUpload() {
      this.showUploadPanel = true
    },
    
    // 上传相关方法（复用您现有的上传逻辑）
    handleFileSelect(file) {
      if (this.validateFile(file)) {
        const fileWithStatus = {
          name: file.name,
          size: file.size,
          type: file.type,
          raw: file.raw,
          status: 'pending'
        }
        this.selectedFiles.push(fileWithStatus)
      }
      return false
    },
    
    validateFile(file) {
      const actualFile = file.raw || file
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp']
      const maxSize = 10 * 1024 * 1024
      
      if (!allowedTypes.includes(actualFile.type)) {
        this.$message.error(`文件 "${actualFile.name}" 不支持，请选择图片文件`)
        return false
      }
      
      if (actualFile.size > maxSize) {
        this.$message.error(`文件 "${actualFile.name}" 大小超过10MB限制`)
        return false
      }
      
      return true
    },
    
    getFilePreview(file) {
      try {
        const actualFile = file.raw || file
        if (actualFile instanceof File || actualFile instanceof Blob) {
          return URL.createObjectURL(actualFile)
        }
        return ''
      } catch (error) {
        console.error('生成预览URL失败:', error)
        return ''
      }
    },
    
    removeSingleFile(index) {
      this.selectedFiles.splice(index, 1)
    },
    
    clearAllFiles() {
      this.selectedFiles.forEach(file => {
        if (file.url) {
          URL.revokeObjectURL(file.url)
        }
      })
      this.selectedFiles = []
    },
    
    cancelUpload() {
      this.showUploadPanel = false
      this.resetUploadForm()
    },
    
    resetUploadForm() {
      this.selectedFiles.forEach(file => {
        if (file.url) {
          URL.revokeObjectURL(file.url)
        }
      })
      this.selectedFiles = []
      this.uploadForm = {
        name: '',
        description: '',
        tags: '',
        categoryId: null
      }
      this.isUploading = false
      this.uploadProgress = 0
      this.uploadError = ''
    },
    
   async handleSubmitUpload() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请选择要上传的图片')
        return
      }
      
      if (!this.validateUpload()) {
        return
      }
      
      this.isUploading = true
      this.uploadProgress = 0
      this.uploadError = ''
      
      try {
        let successCount = 0
        const totalFiles = this.selectedFiles.length
        
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i]
          
          try {
            // 1. 前端直传OSS
            this.uploadProgress = Math.round((i / totalFiles) * 70)
            this.$message.info(`正在上传: ${file.name}`)
            
            const uploadResult = await this.uploadFileToOSS(file)
            
            if (uploadResult.code === 200 && uploadResult.data.success) {
              // 2. 保存图片信息到应用端
              this.uploadProgress = Math.round((i / totalFiles) * 70 + 30)
              await this.saveImageInfo(file, uploadResult.data)
              successCount++
              
              this.$message.success(`图片 ${file.name} 上传成功`)
            } else {
              throw new Error(uploadResult.message || 'OSS上传失败')
            }
          } catch (error) {
            console.error(`文件 ${file.name} 上传失败:`, error)
            this.uploadError = `文件 ${file.name} 上传失败: ${error.message}`
            this.$message.error(`图片 ${file.name} 上传失败`)
          }
        }
        
        this.uploadProgress = 100
        
        if (successCount > 0) {
          this.$message.success(`成功上传 ${successCount}/${totalFiles} 张图片`)
          this.showUploadPanel = false
          this.resetUploadForm()
          // 关键：上传成功后重新加载图片列表
          this.loadImages()
        } else {
          this.$message.error('所有图片上传失败，请检查网络或文件格式')
        }
        
      } catch (error) {
        console.error('上传过程出错:', error)
        this.uploadError = error.message
        this.$message.error('上传过程发生错误')
      } finally {
        this.isUploading = false
      }
    },
    
    async uploadFileToOSS(file) {
      const actualFile = file.raw || file
      return await uploadImageToOSS(actualFile, this.tenantId)
    },
    
    async saveImageInfo(file, ossData) {
      console.log(ossData);
      const imageData = {
        name: this.uploadForm.name || file.name.replace(/\.[^/.]+$/, ""), // 去掉扩展名
        originalFilename: file.name,
        ossObjectKey: ossData.objectKey,
        filePath: ossData.fileKey, // 使用OSS key作为文件路径
        fileSize: file.size,
        mimeType: file.type,
        description: this.uploadForm.description,
        tags: this.uploadForm.tags,
        categoryCode: this.uploadForm.categoryId, // 注意：这里是 categoryId 不是 categoryCode
        tenantId: this.tenantId,
        createdBy: 'admin'
      }
      
      try {
        const response = await saveImageInfo(imageData)
        return response
      } catch (error) {
        throw new Error('保存图片信息失败: ' + (error.message || '未知错误'))
      }
    },
    
    // 工具方法
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    formatTime(timeArray) {
      if (!timeArray || !Array.isArray(timeArray) || timeArray.length < 3) {
        return ''
      }
      
      try {
        // 数组格式: [年, 月, 日, 时, 分, 秒, 纳秒]
        let year = timeArray[0]
        let month = timeArray[1]
        let day = timeArray[2]
        let hour = timeArray[3] || 0
        let minute = timeArray[4] || 0
        
        // 转换为北京时间 (UTC+8)
        hour = hour + 8
        if (hour >= 24) {
          hour = hour - 24
          day = day + 1
          // 处理月份和年份的进位
          if (day > new Date(year, month, 0).getDate()) {
            day = 1
            month = month + 1
            if (month > 12) {
              month = 1
              year = year + 1
            }
          }
        }
        
        const monthStr = String(month).padStart(2, '0')
        const dayStr = String(day).padStart(2, '0')
        const hourStr = String(hour).padStart(2, '0')
        const minuteStr = String(minute).padStart(2, '0')
        
        return `${year}-${monthStr}-${dayStr} ${hourStr}:${minuteStr}`
      } catch (e) {
        console.error('时间格式化错误:', e)
        return ''
      }
    },

    validateUpload() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请选择要上传的图片')
        return false
      }
      
      // 检查是否有重复文件名
      const fileNames = this.selectedFiles.map(f => f.name)
      const uniqueNames = new Set(fileNames)
      if (uniqueNames.size !== fileNames.length) {
        this.$message.warning('存在重复文件名，请修改后重新上传')
        return false
      }
      
      return true
    },    
  }
}
</script>

<style scoped>
.image-library-selector {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.selector-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

.category-filter {
  margin-bottom: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.image-grid-container {
  flex: 1;
  min-height: 400px;
  overflow-y: auto;
}

.loading-container, .empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #909399;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.image-selector-item {
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  background: #fff;
  cursor: pointer;
  position: relative;
}

.image-selector-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.image-selector-item.selected {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 120px;
  overflow: hidden;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.image-selector-item:hover .image-wrapper img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-selector-item:hover .image-overlay {
  opacity: 1;
}

.image-actions {
  display: flex;
  gap: 8px;
}

.selected-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #409eff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.image-info {
  padding: 8px;
}

.image-name {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-meta {
  font-size: 11px;
  color: #909399;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.selector-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f7fa;
  border-top: 1px solid #e4e7ed;
  margin-top: auto;
}

.footer-info {
  color: #606266;
}

.selected-count {
  color: #409eff;
  font-weight: 600;
}

.footer-actions {
  display: flex;
  gap: 8px;
}

/* 上传面板样式 */
.upload-panel {
  max-height: 70vh;
  overflow-y: auto;
}

.upload-area {
  width: 100%;
  min-height: 200px;
}

.selected-files-container {
  margin-top: 0;
}

.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.file-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.file-preview-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.preview-container {
  position: relative;
  width: 100%;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
  margin-bottom: 8px;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.preview-container:hover .preview-overlay {
  opacity: 1;
}

.file-info {
  text-align: center;
}

.file-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.file-size {
  font-size: 11px;
  color: #909399;
}

.image-info-form {
  margin-top: 20px;
}

.upload-progress {
  margin-top: 20px;
}

.upload-status {
  margin-top: 8px;
  text-align: center;
}

.uploading-text, .success-text, .error-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
}

/* 预览样式 */
.image-preview {
  display: flex;
  gap: 20px;
}

.preview-img {
  max-width: 60%;
  max-height: 60vh;
  object-fit: contain;
}

.preview-info {
  flex: 1;
}

.preview-info h3 {
  margin-top: 0;
  margin-bottom: 16px;
}

.preview-info p {
  margin-bottom: 12px;
}

.image-wrapper img:after {
  content: "图片加载失败";
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ccc;
  font-size: 12px;
}
</style>